import { EmailVerificationToken } from '@/core/models/email-verification-token.model';
import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TextBoxComponent } from '@progress/kendo-angular-inputs';
import { NotificationService } from '@progress/kendo-angular-notification';
import { Subscription } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { AuthenticationConfig } from '../../authentication.module';
import { PasswordValidator } from '../../validators/password.validator';
import { PasswordMatchValidator } from '../../validators/passwordmatch.validator';
import { PortalkonfigurationDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/portalkonfiguration-display-model';
import { TokenExpiredNotification } from '@/core/utils/notifications/authentication/token-expired.notification';
import { ExtendedAuthenticationService } from '../../services/extended-authentication.service';
import { AccessToken } from '@/core/models/access-token.model';
import { IdToken } from '@/core/models/id-token.model';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';
import { AUTHENTICATION_CONFIG } from '../../injection-tokens';
import { RegisterUserCommand } from '@/api/models/ace/xFound/Portal/Core/CAQ/Authentication/Commands/register-user-command';
import { AnredeDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/anrede-display-model';
import { TitelDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/titel-display-model';
import { StammdatenService } from '@/core/services/stammdaten.service';
import { I18nService } from '@/core/services/i18nService';
import { VerifyRegistrationRequest } from '@/api/model-index';

@Component({
  selector: 'register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit, AfterViewInit, OnDestroy {
  error: string;
  registrationForm: UntypedFormGroup;
  redirect: string;
  registerSubscription: Subscription;
  tokenClaims: any;
  registerCommand = new RegisterUserCommand();
  protected portalkonfiguration: PortalkonfigurationDisplayModel;
  public anredeItems: AnredeDisplayModel[];
  public titelItems: TitelDisplayModel[];
  protected languageSubscription: Subscription;
  public isPasswordFieldVisible: boolean = true;


  @ViewChild("password") public passwordTextBox: TextBoxComponent;
  @ViewChild("passwordW") public passwordWiederholenTextBox: TextBoxComponent;

  public get anredeFormControl(): AbstractControl {
    return this.registrationForm.controls["anredeId"];
  }

  public get titelFormControl(): AbstractControl {
    return this.registrationForm.controls["titelId"];
  }

  public get vornameFormControl(): AbstractControl {
    return this.registrationForm.controls["vorname"];
  }

  public get nachnameFormControl(): AbstractControl {
    return this.registrationForm.controls["nachname"];
  }

  public get emailFormControl(): AbstractControl {
    return this.registrationForm.controls["email"];
  }

  public get passwortFormControl(): AbstractControl {
    return this.registrationForm.controls["passwort"];
  }

  public get passwortWiederholenFormControl(): AbstractControl {
    return this.registrationForm.controls["passwortWiederholen"];
  }

  public get anredeErrorMessages(): string[] {
    let validationErrors: ValidationErrors | null = this.anredeFormControl.errors;

    if (!validationErrors)
      return [];

    return this.toAnredeErrorMessage(validationErrors);
  }

  public get titelErrorMessages(): string[] {
    let validationErrors: ValidationErrors | null = this.titelFormControl.errors;

    if (!validationErrors)
      return [];

    return this.toTitelErrorMessage(validationErrors);
  }

  public get vornameErrorMessages(): string[] {
    let validationErrors: ValidationErrors | null = this.vornameFormControl.errors;

    if (!validationErrors)
      return [];

    return this.toVornameErrorMessage(validationErrors);
  }

  public get nachnameErrorMessages(): string[] {
    let validationErrors: ValidationErrors | null = this.nachnameFormControl.errors;

    if (!validationErrors)
      return [];

    return this.toNachnameErrorMessage(validationErrors);
  }

  public get emailErrorMessages(): string[] {
    let validationErrors: ValidationErrors | null = this.emailFormControl.errors;

    if (!validationErrors)
      return [];

    return this.toEmailErrorMessage(validationErrors);
  }

  public get passwortErrorMessages(): string[] {
    let validationErrors: ValidationErrors | null = this.passwortFormControl.errors;

    if (!validationErrors)
      return [];

    return this.toPasswortErrorMessage(validationErrors);
  }

  public get passwortWiederholenErrorMessages(): string[] {
    let validationErrors: ValidationErrors | null = this.passwortWiederholenFormControl.errors;

    if (!validationErrors)
      return [];

    return this.toPasswortWiderholenErrorMessage(validationErrors);
  }

  constructor(protected router: Router,
    protected route: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    protected authenticationService: ExtendedAuthenticationService,
    protected stammdatenService: StammdatenService,
    protected notificationService: NotificationService,
    protected loadingAnimationService: LoadingAnimationService,
    @Inject(AUTHENTICATION_CONFIG) protected authenticationConfig: AuthenticationConfig,
    protected languageService: I18nService) {
    this.portalkonfiguration = this.route.snapshot.parent?.data.portalkonfiguration;

    this.stammdatenService.GetTitel().subscribe(titles => {
      this.titelItems = (<TitelDisplayModel[]>titles).filter(s => !s.isPostfixTitel);
    });

    this.createForm();
  }

  ngAfterViewInit(): void {
    if (this.isPasswordFieldVisible) {
      this.passwordTextBox.input.nativeElement.type = "password";
      this.passwordWiederholenTextBox.input.nativeElement.type = "password";
    }
  }

  ngOnInit(): void {
    this.loadAnreden();

    this.route.queryParams.subscribe((params) => {
      let email = "";
      try {
        let emailVerificationToken = new EmailVerificationToken(params.token);
        email = emailVerificationToken.email;
        if (emailVerificationToken.isExpired)
          throw new TokenExpiredNotification();
        this.registerCommand.verificationToken = emailVerificationToken.toJwtToken();
        this.registerCommand.email = emailVerificationToken.email;

        this.registrationForm.controls.email.setValue(emailVerificationToken.email);

        if (emailVerificationToken.anredeId || emailVerificationToken.titelId || emailVerificationToken.firstname || emailVerificationToken.lastname) {
          this.registrationForm.controls.anredeId.setValue(emailVerificationToken.anredeId);
          this.registrationForm.controls.titelId.setValue(emailVerificationToken.titelId);
          this.registrationForm.controls.vorname.setValue(emailVerificationToken.firstname);
          this.registrationForm.controls.nachname.setValue(emailVerificationToken.lastname);

          this.registrationForm.controls.anredeId.disable();
          this.registrationForm.controls.titelId.disable();
          this.registrationForm.controls.vorname.disable();
          this.registrationForm.controls.nachname.disable();

          this.isPasswordFieldVisible = false;
        }

      } catch (error) {
        if (error instanceof TokenExpiredNotification)
          this.tokenExpiredHandler(email);
        else if (error instanceof Error)
          throw new Error("invalid token");
      }
    });

    this.languageSubscription = this.languageService.languageChanged$.subscribe(() => {
      this.loadAnreden();
    });
  }

  protected loadAnreden(): void {
    this.stammdatenService.GetAnreden().subscribe(anreden => {
      this.anredeItems = anreden.filter(anrede => anrede.localeCode === this.languageService.language || anrede.localeCode === null);
      if (this.registrationForm.controls.anredeId.value != null) {
        let anrede = anreden.find(x => x.anredeId == this.registrationForm.controls.anredeId.value);
        if (anrede && !this.anredeItems.some(x => x.anredeId == anrede?.anredeId)) {
          this.anredeItems.push(anrede);
        }
      }
    });
  }

  protected tokenExpiredHandler(email: string) {
    let request = new VerifyRegistrationRequest();
    request.email = email;
    
    let subscription = this.authenticationService.apiAuthenticationVerifyRegistrationPost(request.AsRequestBody()).subscribe(()=> {
        let message = this.languageService.instant('pre-register.success.emailinformation');
        this.notificationService.show({
          content: message,
          animation: { type: "fade", duration: 500 },
          position: { horizontal: "center", vertical: "bottom" },
          type: { style: "success", icon: true },
        });
      let promise = this.router.navigateByUrl('/authentication/mailconfirmation?email=' + request.email + '&from=pre-register');
      this.loadingAnimationService.showSpinnerUsingPromise(promise);
    });

    this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
  }

  register() {
    if (this.registrationForm.controls.anredeId.value && this.registrationForm.controls.anredeId.value !== "")
      this.registerCommand.anredeId = this.registrationForm.controls.anredeId.value;

    if (this.registrationForm.controls.titelId.value && this.registrationForm.controls.titelId.value !== "")
      this.registerCommand.titelId = this.registrationForm.controls.titelId.value;

    this.registerCommand.firstname = this.registrationForm.controls.vorname.value;
    this.registerCommand.lastname = this.registrationForm.controls.nachname.value;
    this.registerCommand.password = this.registrationForm.controls.passwort.value;

    this.registerCommand.scope = this.authenticationConfig.scope;
    this.registerCommand.sprache = this.languageService.GetCurrLanguageAsEnum();
    
    this.registerSubscription = this.authenticationService.apiAuthenticationRegisterPost(this.registerCommand.AsRequestBody())
      .pipe(map(idToken => {
        if (idToken)
          this.authenticationService.saveIdToken(new IdToken(idToken));

        return;
      }))
      .pipe(finalize(() => {
        sessionStorage.removeItem("VerificationToken");
        this.registrationForm.markAsPristine();
      }))
      .subscribe(result => {
        let promise = this.router.navigateByUrl(this.authenticationConfig.defaultRedirectUrl);

      this.loadingAnimationService.showSpinnerUsingPromise(promise);
    }, (error => {
      let message = this.languageService.instant('smth_didntwork');

        if (error && error.errorMessages && error.errorMessages.length > 0) {
          message = error.errorMessages[0];
        }

        this.notificationService.show({
          content: message,
          animation: { type: "fade", duration: 500 },
          position: { horizontal: "center", vertical: "bottom" },
          type: { style: "error", icon: true },
        });
      }));

    this.loadingAnimationService.showSpinnerUsingSubscription(this.registerSubscription);
  }

  public get isLoading(): boolean {
    return this.registerSubscription && !this.registerSubscription.closed;
  }

  public get anredeValidated(): boolean {
    return this.registrationForm.controls.anredeId.valid || this.registrationForm.controls.anredeId.untouched;
  }
  public get titelValidated(): boolean {
    return this.registrationForm.controls.titelId.valid || this.registrationForm.controls.titelId.untouched;
  }
  public get emailValidated(): boolean {
    return this.registrationForm.controls.email.valid || this.registrationForm.controls.email.untouched;
  }
  public get passwortValidated(): boolean {
    return this.registrationForm.controls.passwort.valid || this.registrationForm.controls.passwort.untouched;
  }
  public get vornameValidated(): boolean {
    return this.registrationForm.controls.vorname.valid || this.registrationForm.controls.vorname.untouched;
  }
  public get nachnameValidated(): boolean {
    return this.registrationForm.controls.nachname.valid || this.registrationForm.controls.nachname.untouched;
  }
  public get passwortWiederholenValidated(): boolean {
    return this.registrationForm.controls.passwortWiederholen.valid || this.registrationForm.controls.passwortWiederholen.untouched;
  }
  public get passwortError(): ValidationErrors | null {
    return this.registrationForm.controls.passwort.errors
  }

  protected toAnredeErrorMessage(validationErrors: ValidationErrors): string[] {
    let errorMessages: string[] = [];

    for (let key in validationErrors) {
      switch (key) {
        case "required":
          errorMessages.push(this.languageService.instant("register.salutation_required"));
          break;
        default:
          errorMessages.push(this.languageService.instant("register.invalid"));
      }
    }

    return errorMessages;
  }

  protected toTitelErrorMessage(validationErrors: ValidationErrors): string[] {
    let errorMessages: string[] = [];

    for (let key in validationErrors) {
      switch (key) {
        case "required":
          errorMessages.push(this.languageService.instant("register.title_required"));
          break;
        default:
          errorMessages.push(this.languageService.instant("register.invalid"));
      }
    }

    return errorMessages;
  }

  protected toVornameErrorMessage(validationErrors: ValidationErrors): string[] {
    let errorMessages: string[] = [];

    for (let key in validationErrors) {
      switch (key) {
        case "required":
          errorMessages.push(this.languageService.instant("register.firstname_required"));
          break;
        default:
          errorMessages.push(this.languageService.instant("register.invalid"));
      }
    }

    return errorMessages;
  }

  protected toNachnameErrorMessage(validationErrors: ValidationErrors): string[] {
    let errorMessages: string[] = [];

    for (let key in validationErrors) {
      switch (key) {
        case "required":
          errorMessages.push(this.languageService.instant("register.lastname_required"));
          break;
        default:
          errorMessages.push(this.languageService.instant("register.invalid"));
      }
    }

    return errorMessages;
  }

  protected toEmailErrorMessage(validationErrors: ValidationErrors): string[] {
    let errorMessages: string[] = [];

    for (let key in validationErrors) {
      switch (key) {
        case "required":
          errorMessages.push(this.languageService.instant("register.email_required"));
          break;
        case "email":
          errorMessages.push(this.languageService.instant("register.email_invalid"));
            break;
        default:
          errorMessages.push(this.languageService.instant("register.invalid"));
      }
    }

    return errorMessages;
  }

  protected toPasswortErrorMessage(validationErrors: ValidationErrors): string[] {
    let errorMessages: string[] = [];

    for (let key in validationErrors) {
      switch (key) {
        case "required":
          errorMessages.push(this.languageService.instant("register.password_required"));
          break;
        case "minlength":
          errorMessages.push(this.languageService.instant("register.passwordformat_lenght_required"));
          break;
        case "notStrongEnough":
          errorMessages.push(this.languageService.instant("register.passwordformat_strenght_required"));
            break;
        default:
          errorMessages.push(this.languageService.instant("register.invalid"));
      }
    }

    return errorMessages;
  }

  protected toPasswortWiderholenErrorMessage(validationErrors: ValidationErrors): string[] {
    let errorMessages: string[] = [];

    for (let key in validationErrors) {
      switch (key) {
        case "required":
          errorMessages.push(this.languageService.instant('register.password_required'));
          break;
        case "passwordMatch":
          errorMessages.push(this.languageService.instant('register.password_equalrequired'));
          break;
        default:
          errorMessages.push(this.languageService.instant('register.invalid'));
      }
    }

    return errorMessages;
  }

  protected createForm() {
    this.registrationForm = this.formBuilder.group({
      anredeId: ['', []],
      titelId: ['', []],
      email: ['', [Validators.required, Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/)]],
      passwort: ['', [Validators.required, Validators.minLength(8), new PasswordValidator(this.languageService)]],
      vorname: ['', [Validators.required, Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/)]],
      nachname: ['', [Validators.required, Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/)]],
      passwortWiederholen: ['', [Validators.required]],
    }, { validators: PasswordMatchValidator("passwort", "passwortWiederholen") });
  }

  ngOnDestroy() {
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }

    
  }

}
